import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardBody, Button, Spinner, Checkbox } from "@heroui/react";
import { LuArrowLeft, LuSave } from "react-icons/lu";
import { useFetchData, usePostData } from "@/hooks";
import { formatDate, getTodayDate, showToast } from "@/utils";
import {
    EmptyState,
    ErrorState,
    PageHeader,
    ThemedText,
} from "@/components/ui";
import { StockDistributionModal } from "./components";

const ZAK_TO_KG = 50; // 1 zak = 50 kg
const BUNDLE_TO_PCS = 300; // 1 ikat = 300 butir
const TRAY_TO_PCS = 30; // 1 tray/papan = 30 butir
const DAYS_RANGE = 30; // Show 30 days before and after today
const INPUT_CLASS =
    "focus:border-primary w-full rounded border border-zinc-300 bg-zinc-100 px-2 py-1 text-sm focus:outline-none dark:border-zinc-600 dark:bg-zinc-800";

const TabelProduksiTelur = () => {
    const { barrackId, batchNumber } = useParams();
    const navigate = useNavigate();
    const tableContainerRef = useRef(null);

    // Local state for editable production data
    const [productionData, setProductionData] = useState([]);
    // Show all data toggle
    const [showAll, setShowAll] = useState(false);

    // Distribution Modal State
    const [isDistributionModalOpen, setIsDistributionModalOpen] =
        useState(false);
    const [productionBreakdown, setProductionBreakdown] = useState({
        bundles: 0,
        trays: 0,
        pieces: 0,
    });
    const [pendingDataToUpdate, setPendingDataToUpdate] = useState(null);

    const fetchProduction = useFetchData(
        `barracks/${barrackId}/batches/${batchNumber}/production-data`,
    );

    const getPakanAyam = useFetchData(`select/chicken-feeds`);
    const pakanAyamOptions = getPakanAyam.data || [];

    const updateProduction = usePostData(
        `barracks/${barrackId}/update-production`,
    );

    const submitData = (data, distribution = []) => {
        updateProduction.mutate(
            {
                data: data,
                distribution: distribution,
            },
            {
                onSuccess: () => {
                    showToast(
                        "success",
                        "Berhasil",
                        "Data produksi berhasil disimpan.",
                    );
                    setProductionData((prev) =>
                        prev.map((item) => ({ ...item, is_modified: 0 })),
                    );
                    setPendingDataToUpdate(null);
                    setIsDistributionModalOpen(false);
                    // Refetch to ensure original data is updated for next calculation
                    fetchProduction.refetch();
                    // Scroll back to today's row after save
                    setTimeout(() => {
                        const today = getTodayDate();
                        const element = document.getElementById(`row-${today}`);
                        if (element) {
                            element.scrollIntoView({ behavior: "smooth", block: "center" });
                        }
                    }, 500);
                },
                onError: (err) => {
                    showToast(
                        "error",
                        "Gagal",
                        "Terjadi kesalahan saat menyimpan data. " +
                            (err.response?.data?.message || err.message),
                    );
                },
            },
        );
    };

    const handleSave = () => {
        const dataToUpdate = productionData.filter(
            (item) => item.is_modified === 1,
        );

        if (dataToUpdate.length === 0) {
            showToast(
                "warning",
                "Tidak ada perubahan",
                "Tidak ada data yang berubah untuk disimpan.",
            );
            return;
        }

        // Calculate positive delta production per unit
        const breakdown = { bundles: 0, trays: 0, pieces: 0, broken: 0 };
        const originalData = fetchProduction.data || [];

        dataToUpdate.forEach((item) => {
            const originalRow = originalData.find((o) => o.id === item.id);

            // Calculate delta for each unit type
            const oldBundles = originalRow?.bundles || 0;
            const oldTrays = originalRow?.trays || 0;
            const oldPieces = originalRow?.pieces || 0;
            const oldBroken = originalRow?.broken || 0;

            const newBundles = item.bundles || 0;
            const newTrays = item.trays || 0;
            const newPieces = item.pieces || 0;
            const newBroken = item.broken || 0;

            const bundlesDiff = newBundles - oldBundles;
            const traysDiff = newTrays - oldTrays;
            const piecesDiff = newPieces - oldPieces;
            const brokenDiff = newBroken - oldBroken;

            if (bundlesDiff > 0) breakdown.bundles += bundlesDiff;
            if (traysDiff > 0) breakdown.trays += traysDiff;
            if (piecesDiff > 0) breakdown.pieces += piecesDiff;
            if (brokenDiff > 0) breakdown.broken += brokenDiff;
        });

        const hasProduction =
            breakdown.bundles > 0 ||
            breakdown.trays > 0 ||
            breakdown.pieces > 0 ||
            breakdown.broken > 0;

        if (hasProduction) {
            setProductionBreakdown(breakdown);
            setPendingDataToUpdate(dataToUpdate);
            setIsDistributionModalOpen(true);
        } else {
            submitData(dataToUpdate);
        }
    };

    const handleConfirmDistribution = (distributions) => {
        if (pendingDataToUpdate) {
            submitData(pendingDataToUpdate, distributions);
        }
    };

    // Sync API data to local state on load
    useEffect(() => {
        if (fetchProduction.data && fetchProduction.data.length > 0) {
            setProductionData(fetchProduction.data);
        }
    }, [fetchProduction.data]);

    /**
     * Handle population field changes (Masuk, Mati, Afkir)
     * - Masuk: ADD to Sisa for current row and all rows below
     * - Mati/Afkir: SUBTRACT from Sisa for current row and all rows below
     */
    const handlePopulationChange = useCallback((rowIndex, field, newValue) => {
        setProductionData((prevData) => {
            const updatedData = [...prevData];
            const currentRow = { ...updatedData[rowIndex] };

            // Get old value (treat null/undefined as 0)
            const oldValue = currentRow[field] || 0;
            const parsedNewValue = newValue || 0;

            // Calculate the difference
            let difference = parsedNewValue - oldValue;

            // For Mati and Afkir, we subtract (so invert the difference)
            if (field === "mortality" || field === "culling") {
                difference = -difference;
            }

            // Update the current field value
            // Store as null if 0 to keep it clean, or keep 0.
            // Using null for consistency with API often returning null for empty.
            currentRow[field] = parsedNewValue === 0 ? null : parsedNewValue;
            updatedData[rowIndex] = currentRow;

            // Apply the difference to Sisa (population_remaining) for current row and all below
            for (let i = rowIndex; i < updatedData.length; i++) {
                const row = { ...updatedData[i] };
                const currentRemaining = row.population_remaining || 0;
                row.population_remaining = currentRemaining + difference;
                row.is_modified = 1;
                updatedData[i] = row;
            }

            return updatedData;
        });
    }, []);

    /**
     * Handle Feed Zak changes
     * - Update feed_zak
     * - Calculate feed_kg = zak * 50
     * - Calculate FCR (feed_percentage) = (feed_kg / population_remaining) * 100
     */
    const handleFeedChange = useCallback((rowIndex, newValue) => {
        setProductionData((prevData) => {
            const updatedData = [...prevData];
            const currentRow = { ...updatedData[rowIndex] };

            const zak = parseFloat(newValue) || 0;
            // Store as null if 0 for cleaner UI, matching other inputs
            currentRow.feed_zak = zak === 0 ? null : zak;

            // Calculate KG (Assuming 1 Zak = 50kg as per user requirement)
            const kg = zak * ZAK_TO_KG;
            currentRow.feed_kg = kg === 0 ? 0 : parseFloat(kg.toFixed(2));

            // Calculate FCR / Percentage
            // Formula from user: pakan_kg / sisa_populasi * 100
            const population = currentRow.population_remaining || 0;
            let percentage = 0;
            if (population > 0) {
                percentage = (kg / population) * 100;
            }
            currentRow.feed_percentage =
                percentage === 0 ? 0 : parseFloat(percentage.toFixed(2));

            currentRow.is_modified = 1;
            updatedData[rowIndex] = currentRow;
            return updatedData;
        });
    }, []);

    /**
     * Handle Production field changes (Ikat, Tray, Butir, Pecah)
     * - Update field value
     * - Calculate Total Production (Ikat*300 + Tray*30 + Butir + Pecah)
     * - Cascade update Accumulation, Hen Day (HD), and Hen House (HH)
     */
    const handleProductionChange = useCallback((rowIndex, field, newValue) => {
        setProductionData((prevData) => {
            const updatedData = [...prevData];
            const currentRow = { ...updatedData[rowIndex] };

            const parsedValue = parseFloat(newValue) || 0;
            currentRow[field] =
                newValue === 0 || newValue === "" ? null : parsedValue;

            // Update row in array before calculation loop to ensure it's used
            updatedData[rowIndex] = currentRow;

            // Get Initial Population from first row for Hen House calc
            const initialPopulation = updatedData[0]?.population_in || 0;

            // Loop from current row to end to cascade calculations
            for (let i = rowIndex; i < updatedData.length; i++) {
                const row = { ...updatedData[i] };

                // Calculate Total Production for this row
                const bundles = row.bundles || 0;
                const trays = row.trays || 0;
                const pieces = row.pieces || 0;
                const broken = row.broken || 0;

                const total =
                    bundles * BUNDLE_TO_PCS +
                    trays * TRAY_TO_PCS +
                    pieces +
                    broken;
                row.production_total = total;

                // Calculate Accumulated Production
                // If first row, accumulation = total. Else prevAccumulation + total.
                const prevAccumulation =
                    i > 0 ? updatedData[i - 1].production_accumulated || 0 : 0;
                const accumulated = prevAccumulation + total;
                row.production_accumulated = accumulated;

                // Calculate Hen Day (HD) = (Total / Population Remaining) * 100
                const populationRemaining = row.population_remaining || 0;
                let hd = 0;
                if (populationRemaining > 0) {
                    hd = (total / populationRemaining) * 100;
                }
                row.hen_day = hd === 0 ? 0 : parseFloat(hd.toFixed(2));

                // Calculate Hen House (HH) = (Accumulated / Initial Population) * 100
                let hh = 0;
                if (initialPopulation > 0) {
                    hh = (accumulated / initialPopulation) * 100;
                }
                row.hen_house = hh === 0 ? 0 : parseFloat(hh.toFixed(2));

                row.is_modified = 1;
                updatedData[i] = row;
            }

            return updatedData;
        });
    }, []);

    // Find today's row index for auto-scroll
    const todayIndex = useMemo(() => {
        const today = getTodayDate();
        return productionData.findIndex((item) => item.date === today);
    }, [productionData]);

    // Filter data logic
    const filteredData = useMemo(() => {
        if (!productionData.length) return [];

        if (showAll) return productionData;

        if (todayIndex === -1) {
            // If today is not in range (maybe future or past batch), show first 60 rows
            return productionData.slice(0, 60);
        }

        const start = Math.max(0, todayIndex - DAYS_RANGE);
        const end = Math.min(
            productionData.length,
            todayIndex + DAYS_RANGE + 1,
        );

        return productionData.slice(start, end);
    }, [productionData, todayIndex, showAll]);

    // Auto-scroll to today's row logic
    useEffect(() => {
        // Only auto-scroll if we found today and data is loaded
        if (todayIndex >= 0 && productionData.length > 0) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                const today = getTodayDate();
                const element = document.getElementById(`row-${today}`);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [todayIndex, productionData.length, showAll]);

    // Check if data is loaded from API but not yet synced to local state
    const isInitializing =
        !fetchProduction.isLoading &&
        fetchProduction.data?.length > 0 &&
        productionData.length === 0;

    if (fetchProduction.isLoading || isInitializing) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Spinner size="lg" label="Loading production data..." />
            </div>
        );
    }

    if (fetchProduction.error) {
        return (
            <div className="flex h-screen items-center justify-center">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data produksi. Silakan coba lagi."
                    onAction={fetchProduction.refetch}
                    actionLabel="Coba Lagi"
                />
            </div>
        );
    }

    return (
        <div className="flex h-full min-w-0 flex-col gap-6">
            <StockDistributionModal
                isOpen={isDistributionModalOpen}
                onClose={() => setIsDistributionModalOpen(false)}
                onConfirm={handleConfirmDistribution}
                productionBreakdown={productionBreakdown}
                isSubmitting={updateProduction.isLoading}
            />
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                <div className="flex items-start gap-3">
                    <Button
                        isIconOnly
                        variant="light"
                        onPress={() => navigate(-1)}
                        className="-ml-2"
                    >
                        <LuArrowLeft size={22} />
                    </Button>
                    <PageHeader
                        title={`${fetchProduction.data?.[0]?.barrack || "Data Produksi"} — ${fetchProduction.data?.[0]?.material_name || "Telur"}`}
                        breadcrumbs={[
                            { label: "Kandang", href: "/kandang" },
                            { label: "Detail Kandang", href: -1 },
                            { label: "Produksi", href: null },
                        ]}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        color="primary"
                        size="sm"
                        startContent={<LuSave size={18} />}
                        onPress={handleSave}
                    >
                        Simpan Perubahan
                    </Button>
                </div>
            </div>

            <Card className="min-w-0 overflow-hidden" shadow="sm">
                <CardBody className="min-w-0 overflow-hidden p-0">
                    <div className="border-default-200 flex flex-col justify-between gap-2 border-b px-4 py-3 sm:flex-row sm:items-center">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                                <div className="bg-primary-100 ring-primary-500 dark:bg-primary-900/50 dark:ring-primary-400 h-4 w-10 shrink-0 rounded ring-1" />
                                <ThemedText size="xs" variant="secondary">
                                    Hari Ini
                                </ThemedText>
                            </div>
                            <ThemedText size="sm" variant="secondary">
                                Batch #{batchNumber} - Menampilkan data{" "}
                                {DAYS_RANGE} hari sebelum & sesudah hari ini
                            </ThemedText>
                        </div>
                        <Checkbox
                            isSelected={showAll}
                            onValueChange={(isSelected) => {
                                if (isSelected) {
                                    showToast(
                                        "warning",
                                        "Perhatian",
                                        "Menampilkan semua data mungkin akan menyebabkan aplikasi terasa lambat.",
                                    );
                                }
                                setShowAll(isSelected);
                            }}
                            size="sm"
                        >
                            Tampilkan semua data
                        </Checkbox>
                    </div>

                    {!productionData || productionData.length === 0 ? (
                        <EmptyState
                            title="Data Produksi Kosong"
                            description="Belum ada data produksi untuk batch dan kandang ini."
                        />
                    ) : (
                        <div
                            ref={tableContainerRef}
                            className="max-w-full min-w-0 overflow-auto"
                            style={{ height: "calc(100vh - 180px)" }}
                        >
                            <table className="text-small min-w-max border-collapse">
                                <thead className="bg-default-100 sticky top-0 z-20 shadow-sm">
                                    {/* Header Row 1 */}
                                    <tr className="divide-default-300 border-default-300 bg-content2 divide-x border-b">
                                        <th
                                            rowSpan={2}
                                            className="w-32 px-2 py-3 text-center font-semibold"
                                        >
                                            Tanggal
                                        </th>
                                        <th
                                            rowSpan={2}
                                            className="w-16 px-2 py-3 text-center font-semibold"
                                        >
                                            Umur
                                            <br />
                                            (hari)
                                        </th>
                                        <th
                                            colSpan={4}
                                            className="bg-primary-50/50 px-2 py-1 text-center font-semibold"
                                        >
                                            Populasi Ayam
                                        </th>
                                        <th
                                            colSpan={4}
                                            className="bg-success-50/50 px-2 py-1 text-center font-semibold"
                                        >
                                            Pakan
                                        </th>
                                        <th
                                            colSpan={8}
                                            className="bg-warning-50/50 px-2 py-1 text-center font-semibold"
                                        >
                                            Produksi
                                        </th>
                                    </tr>
                                    {/* Header Row 2 */}
                                    <tr className="divide-default-300 border-default-300 bg-content1 divide-x border-b">
                                        {/* Populasi */}
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Masuk
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Mati
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Afkir
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Sisa
                                        </th>

                                        {/* Pakan */}
                                        <th className="w-52 px-2 py-2 text-center text-xs">
                                            Jenis
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Zak
                                        </th>
                                        <th className="w-16 px-2 py-2 text-center text-xs">
                                            Kg
                                        </th>
                                        <th className="w-16 px-2 py-2 text-center text-xs">
                                            FCR
                                        </th>

                                        {/* Produksi */}
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Ikat
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Papan
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Butir
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Pecah
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs font-bold">
                                            Total
                                        </th>
                                        <th className="w-20 px-2 py-2 text-center text-xs">
                                            Akumulasi
                                        </th>
                                        <th className="w-16 px-2 py-2 text-center text-xs">
                                            % HD
                                        </th>
                                        <th className="w-16 px-2 py-2 text-center text-xs">
                                            % HH
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-default-200 divide-y">
                                    {filteredData.map((item) => {
                                        const index = productionData.findIndex(
                                            (d) => d.id === item.id,
                                        );
                                        const isToday =
                                            item.date === getTodayDate();
                                        return (
                                            <tr
                                                key={item.id}
                                                id={`row-${item.date}`}
                                                className={`divide-default-200 hover:bg-default-50 divide-x ${
                                                    isToday
                                                        ? "bg-primary-50 ring-primary-500 dark:bg-primary-900/30 dark:ring-primary-400"
                                                        : index % 2 === 0
                                                          ? "bg-transparent"
                                                          : "bg-default-50"
                                                }`}
                                            >
                                                <td className="px-2 py-1 text-center whitespace-nowrap">
                                                    <ThemedText size="sm">
                                                        {formatDate(item.date)}
                                                    </ThemedText>
                                                </td>
                                                <td className="px-2 py-1 text-center font-medium">
                                                    <ThemedText size="sm">
                                                        {item.age}
                                                    </ThemedText>
                                                </td>

                                                {/* Populasi - Native HTML Inputs */}
                                                <td className="p-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        aria-label="Jumlah ayam masuk"
                                                        className={`${INPUT_CLASS} text-center`}
                                                        value={
                                                            item.population_in ??
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            handlePopulationChange(
                                                                index,
                                                                "population_in",
                                                                e.target.value
                                                                    ? parseFloat(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      )
                                                                    : 0,
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="p-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        aria-label="Jumlah ayam mati"
                                                        className={`${INPUT_CLASS} text-center`}
                                                        value={
                                                            item.mortality ?? ""
                                                        }
                                                        onChange={(e) =>
                                                            handlePopulationChange(
                                                                index,
                                                                "mortality",
                                                                e.target.value
                                                                    ? parseFloat(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      )
                                                                    : 0,
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="p-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        aria-label="Jumlah ayam afkir"
                                                        className={`${INPUT_CLASS} text-center`}
                                                        value={
                                                            item.culling ?? ""
                                                        }
                                                        onChange={(e) =>
                                                            handlePopulationChange(
                                                                index,
                                                                "culling",
                                                                e.target.value
                                                                    ? parseFloat(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      )
                                                                    : 0,
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="bg-default-100 px-2 py-1 text-center font-semibold">
                                                    <ThemedText
                                                        size="sm"
                                                        className="text-default-600 font-semibold"
                                                    >
                                                        {item.population_remaining ??
                                                            0}
                                                    </ThemedText>
                                                </td>

                                                {/* Pakan - Native HTML Select & Input */}
                                                <td className="p-1">
                                                    <select
                                                        aria-label="Jenis Pakan"
                                                        className={INPUT_CLASS}
                                                        value={
                                                            item.feed_type_id ??
                                                            ""
                                                        }
                                                        onChange={(e) => {
                                                            setProductionData(
                                                                (prev) => {
                                                                    const newData =
                                                                        [
                                                                            ...prev,
                                                                        ];
                                                                    newData[
                                                                        index
                                                                    ] = {
                                                                        ...newData[
                                                                            index
                                                                        ],
                                                                        feed_type_id:
                                                                            e
                                                                                .target
                                                                                .value ||
                                                                            null,
                                                                        is_modified: 1,
                                                                    };
                                                                    return newData;
                                                                },
                                                            );
                                                        }}
                                                    >
                                                        <option value="">
                                                            Pilih Pakan
                                                        </option>
                                                        {pakanAyamOptions.map(
                                                            (option) => (
                                                                <option
                                                                    key={
                                                                        option.value
                                                                    }
                                                                    value={
                                                                        option.value
                                                                    }
                                                                >
                                                                    {
                                                                        option.title
                                                                    }
                                                                </option>
                                                            ),
                                                        )}
                                                    </select>
                                                </td>
                                                <td className="p-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        aria-label="Jumlah Pakan Zak"
                                                        className={`${INPUT_CLASS} text-center`}
                                                        value={
                                                            item.feed_zak ?? ""
                                                        }
                                                        onChange={(e) =>
                                                            handleFeedChange(
                                                                index,
                                                                e.target.value
                                                                    ? parseFloat(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      )
                                                                    : 0,
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="bg-default-100 px-2 py-1 text-center">
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        {item.feed_kg || "-"}
                                                    </ThemedText>
                                                </td>
                                                <td className="bg-default-100 px-2 py-1 text-center">
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        {item.feed_percentage ||
                                                            "-"}
                                                    </ThemedText>
                                                </td>

                                                {/* Produksi - Native HTML Inputs */}
                                                <td className="p-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        aria-label="Produksi Telur Ikat"
                                                        className={`${INPUT_CLASS} text-center`}
                                                        value={
                                                            item.bundles ?? ""
                                                        }
                                                        onChange={(e) =>
                                                            handleProductionChange(
                                                                index,
                                                                "bundles",
                                                                e.target.value
                                                                    ? parseFloat(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      )
                                                                    : 0,
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="p-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        aria-label="Produksi Telur Tray"
                                                        className={`${INPUT_CLASS} text-center`}
                                                        value={item.trays ?? ""}
                                                        onChange={(e) =>
                                                            handleProductionChange(
                                                                index,
                                                                "trays",
                                                                e.target.value
                                                                    ? parseFloat(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      )
                                                                    : 0,
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="p-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        aria-label="Produksi Telur Butir"
                                                        className={`${INPUT_CLASS} text-center`}
                                                        value={
                                                            item.pieces ?? ""
                                                        }
                                                        onChange={(e) =>
                                                            handleProductionChange(
                                                                index,
                                                                "pieces",
                                                                e.target.value
                                                                    ? parseFloat(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      )
                                                                    : 0,
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="p-1">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        aria-label="Jumlah Telur Pecah"
                                                        className={`${INPUT_CLASS} text-center`}
                                                        value={
                                                            item.broken ?? ""
                                                        }
                                                        onChange={(e) =>
                                                            handleProductionChange(
                                                                index,
                                                                "broken",
                                                                e.target.value
                                                                    ? parseFloat(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      )
                                                                    : 0,
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td className="bg-default-100 px-2 py-1 text-center font-bold">
                                                    <ThemedText
                                                        size="sm"
                                                        className="font-bold"
                                                    >
                                                        {item.production_total ||
                                                            0}
                                                    </ThemedText>
                                                </td>
                                                <td className="bg-default-100 px-2 py-1 text-center">
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        {item.production_accumulated ||
                                                            0}
                                                    </ThemedText>
                                                </td>
                                                <td className="bg-default-100 px-2 py-1 text-center">
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        {item.hen_day
                                                            ? `${item.hen_day}%`
                                                            : "-"}
                                                    </ThemedText>
                                                </td>
                                                <td className="bg-default-100 px-2 py-1 text-center">
                                                    <ThemedText
                                                        size="sm"
                                                        variant="secondary"
                                                    >
                                                        {item.hen_house
                                                            ? `${item.hen_house}%`
                                                            : "-"}
                                                    </ThemedText>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default TabelProduksiTelur;
